import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';

import { Collapse, IconButton } from '@/components/ui-bridge/core';
import { ExpandLess, ExpandMore, Close } from '@/components/ui-bridge/icons';
import PropTypes from 'prop-types';

import ColorButton from '@/components/common/elements/ColorButton';

import classes from './style.module.scss';

const animatedComponents = makeAnimated();

const ModalAIVerification = ({ onClose, validationResults, loading }) => {
  const intl = useIntl();
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedItems, setExpandedItems] = useState({});

  // Opções para os filtros
  const severityOptions = [
    { value: 'CRITICAL', label: intl.formatMessage({ id: 'CRITICAL' }) },
    { value: 'WARNING', label: intl.formatMessage({ id: 'WARNING' }) },
    { value: 'SUGGESTION', label: intl.formatMessage({ id: 'SUGGESTION' }) },
  ];

  const categoryOptions = [
    { value: 'DATA_INCONSISTENCY', label: intl.formatMessage({ id: 'DATA_INCONSISTENCY' }) },
    { value: 'INCOMPLETE_OR_VAGUE_DATA', label: intl.formatMessage({ id: 'INCOMPLETE_OR_VAGUE_DATA' }) },
    { value: 'TEXT_QUALITY', label: intl.formatMessage({ id: 'TEXT_QUALITY' }) },
    { value: 'OTHER', label: intl.formatMessage({ id: 'OTHER' }) },
  ];

  // Estados dos filtros - iniciar com todos selecionados
  const [selectedSeverities, setSelectedSeverities] = useState(severityOptions);
  const [selectedCategories, setSelectedCategories] = useState(categoryOptions);

  const toggleGroup = groupName => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  const toggleItem = itemKey => {
    setExpandedItems(prev => ({
      ...prev,
      [itemKey]: !prev[itemKey],
    }));
  };

  const handleSeverityChange = selectedOptions => {
    setSelectedSeverities(selectedOptions || []);
  };

  const handleCategoryChange = selectedOptions => {
    setSelectedCategories(selectedOptions || []);
  };

  const clearFilters = () => {
    setSelectedSeverities(severityOptions);
    setSelectedCategories(categoryOptions);
  };

  const getSeverityColor = severity => {
    switch (severity) {
      case 'CRITICAL':
        return '#f44336';
      case 'WARNING':
        return '#ff9800';
      case 'SUGGESTION':
        return '#2196f3';
      default:
        return '#757575';
    }
  };

  const getCategoryColor = category => {
    switch (category) {
      case 'DATA_INCONSISTENCY':
        return '#e91e63';
      case 'INCOMPLETE_OR_VAGUE_DATA':
        return '#9c27b0';
      case 'TEXT_QUALITY':
        return '#673ab7';
      case 'OTHER':
        return '#607d8b';
      default:
        return '#757575';
    }
  };

  const handlePrint = () => {
    try {
      const handleAlternativePrint = () => {
        const originalContent = document.body.innerHTML;

        const printContent = `
          <div style="font-family: Arial, sans-serif; margin: 20px; line-height: 1.6; color: #333;">
            <div style="text-align: center; margin-bottom: 30px; border-bottom: 2px solid #ccc; padding-bottom: 20px;">
              <h1 style="margin: 0; color: #1976d2;">${intl.formatMessage({ id: 'AI_VERIFICATION_RESULTS' })}</h1>
            </div>
            
            <div style="margin-bottom: 30px; padding: 15px; background-color: #f5f5f5; border-radius: 5px;">
              <div style="font-weight: bold; margin-bottom: 10px; color: #1976d2;">${intl.formatMessage({ id: 'APPLIED_FILTERS' }) || 'Filtros Aplicados'}:</div>
              <div style="margin-bottom: 5px;"><strong>${intl.formatMessage({ id: 'SEVERITY' })}:</strong> ${selectedSeverities.map(s => s.label).join(', ')}</div>
              <div style="margin-bottom: 5px;"><strong>${intl.formatMessage({ id: 'CATEGORY' })}:</strong> ${selectedCategories.map(c => c.label).join(', ')}</div>
            </div>

            <div style="font-size: 18px; font-weight: bold; margin: 20px 0; text-align: center; color: #1976d2;">
              ${totalIssues} ${intl.formatMessage({ id: totalIssues === 1 ? 'ISSUE' : 'ISSUES' })}
            </div>

            ${!validationResults || validationResults.length === 0 ? 
              `<div style="text-align: center; font-style: italic; color: #666; margin: 20px 0;">${intl.formatMessage({ id: 'NO_ISSUES_FOUND' })}</div>` :
              validationResults.map(group => {
                const allGroupItems = group.items.filter(item =>
                  item.issues.some(issue =>
                    selectedSeverityValues.includes(issue.severity) && selectedCategoryValues.includes(issue.category)
                  )
                );
                
                if (allGroupItems.length === 0) return '';

                return `
                  <div style="margin-bottom: 20px; border: 1px solid #ddd; border-radius: 3px;">
                    <div style="background-color: #f0f0f0; padding: 10px; font-weight: bold; font-size: 16px; border-bottom: 1px solid #ddd;">${group.index && group.index !== '0' ? `${group.index}. ` : ''}${group.group}</div>
                    ${allGroupItems.map(item => {
                      const visibleIssues = item.issues.filter(issue =>
                        selectedSeverityValues.includes(issue.severity) && selectedCategoryValues.includes(issue.category)
                      );

                      return `
                        <div style="margin: 10px; border-left: 2px solid #1976d2; padding-left: 10px;">
                          <div style="font-weight: bold; margin-bottom: 5px; font-size: 14px;">
                            ${item.index && item.index !== '0' ? `${item.index}. ` : ''}${item.name} (${visibleIssues.length} ${intl.formatMessage({ id: visibleIssues.length === 1 ? 'ISSUE' : 'ISSUES' })})
                          </div>
                          
                          ${visibleIssues.map(issue => `
                            <div style="margin: 5px 0; padding: 5px; border: 1px solid #eee; border-radius: 2px;">
                              <div style="margin-bottom: 3px;">
                                <span style="display: inline-block; padding: 2px 6px; margin-right: 5px; border-radius: 2px; color: white; font-size: 11px; font-weight: bold; background-color: ${getSeverityColor(issue.severity)}">
                                  ${intl.formatMessage({ id: issue.severity })}
                                </span>
                                <span style="display: inline-block; padding: 2px 6px; margin-right: 5px; border-radius: 2px; color: white; font-size: 11px; font-weight: bold; background-color: ${getCategoryColor(issue.category)}">
                                  ${intl.formatMessage({ id: issue.category })}
                                </span>
                              </div>
                              <div style="font-size: 13px; line-height: 1.3;">${issue.description}</div>
                            </div>
                          `).join('')}

                          ${item.info && (item.info.evaluation || item.info.details || item.info.observation || (item.info.damages && item.info.damages.length > 0)) ? `
                            <div style="margin-top: 8px; padding: 5px; background-color: #f9f9f9; border-radius: 2px;">
                              <div style="font-weight: bold; margin-bottom: 5px; color: #1976d2; font-size: 13px;">${intl.formatMessage({ id: 'INSPECTION_DATA' }) || 'Dados da Vistoria'}</div>
                              
                              ${item.info.damages && item.info.damages.length > 0 ? `
                                <div style="margin-bottom: 3px; font-size: 12px;">
                                  <strong>${intl.formatMessage({ id: 'INSPECTION_DAMAGES' }) || 'Apontamentos'}:</strong>
                                  <div style="margin-left: 10px;">
                                    ${item.info.damages.map(damage => `
                                      <div style="margin: 5px 0; padding: 5px; background-color: #f8f9fa; border-left: 3px solid #dee2e6; border-radius: 3px;">
                                        <div style="display: flex; align-items: center; flex-wrap: wrap; gap: 5px; font-size: 13px;">
                                          <span>${damage.description}</span>
                                          ${damage.tags && damage.tags.length > 0 ? damage.tags.map(tag => `
                                            <span style="display: inline-block; padding: 1px 4px; background-color: #e3f2fd; border-radius: 8px; font-size: 10px; color: #1976d2;">
                                              ${tag}
                                            </span>
                                          `).join('') : ''}
                                          ${damage.resolved === true ? ` <span style="color: #4caf50; font-weight: bold; font-size: 11px;">${intl.formatMessage({ id: 'RESOLVED' }) || '✓ Resolvido'}</span>` : ''}
                                        </div>
                                      </div>
                                    `).join('')}
                                  </div>
                                </div>
                              ` : ''}

                              ${item.info.evaluation ? `
                                <div style="margin-bottom: 3px; font-size: 12px;">
                                  <strong>${intl.formatMessage({ id: 'INSPECTION_EVALUATION' }) || 'Avaliação'}:</strong> 
                                  ${item.info.evaluation}
                                </div>
                              ` : ''}

                              ${item.info.details ? `
                                <div style="margin-bottom: 3px; font-size: 12px;">
                                  <strong>${intl.formatMessage({ id: 'INSPECTION_DETAILS' }) || 'Detalhes'}:</strong>
                                  <ul style="margin: 2px 0; padding-left: 15px;">
                                    ${Object.entries(item.info.details).map(([key, value]) => `
                                      <li style="margin-bottom: 2px;"><strong>${key}:</strong> ${value}</li>
                                    `).join('')}
                                  </ul>
                                </div>
                              ` : ''}

                              ${item.info.observation ? `
                                <div style="margin-bottom: 3px; font-size: 12px;">
                                  <strong>${intl.formatMessage({ id: 'INSPECTION_OBSERVATION' }) || 'Observação'}:</strong> 
                                  ${item.info.observation}
                                </div>
                              ` : ''}
                            </div>
                          ` : ''}
                        </div>
                      `;
                    }).join('')}
                  </div>
                `;
              }).join('')}
            }
          </div>
        `;
        
        document.body.innerHTML = printContent;
        
        window.print();
        
        document.body.innerHTML = originalContent;
      };

      const printWindow = window.open('', '_blank', 'width=800,height=600');
      
      if (!printWindow) {
        console.log('Pop-up foi bloqueado, tentando método alternativo...');
        handleAlternativePrint();
        return;
      }

      const printContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>${intl.formatMessage({ id: 'AI_VERIFICATION_RESULTS' })}</title>
            <style>
              body { 
                font-family: Arial, sans-serif; 
                margin: 15px; 
                line-height: 1.4;
                color: #333;
                font-size: 13px;
              }
              .print-header { 
                text-align: center; 
                margin-bottom: 20px; 
                border-bottom: 2px solid #ccc; 
                padding-bottom: 15px;
              }
              .print-header h1 {
                margin: 0;
                color: #1976d2;
                font-size: 20px;
              }
              .filters-section {
                margin-bottom: 20px;
                padding: 10px;
                background-color: #f5f5f5;
                border-radius: 3px;
                font-size: 12px;
              }
              .filters-title {
                font-weight: bold;
                margin-bottom: 5px;
                color: #1976d2;
              }
              .filter-item {
                margin-bottom: 3px;
              }
              .total-issues {
                font-size: 16px;
                font-weight: bold;
                margin: 15px 0;
                text-align: center;
                color: #1976d2;
              }
              .group-container { 
                margin-bottom: 20px; 
                border: 1px solid #ddd;
                border-radius: 3px;
                break-inside: avoid;
              }
              .group-header { 
                background-color: #f0f0f0; 
                padding: 10px; 
                font-weight: bold; 
                font-size: 16px;
                border-bottom: 1px solid #ddd;
              }
              .item-container { 
                margin: 10px; 
                border-left: 2px solid #1976d2;
                padding-left: 10px;
              }
              .item-title { 
                font-weight: bold; 
                margin-bottom: 5px; 
                font-size: 14px;
              }
              .issue-container { 
                margin: 5px 0; 
                padding: 5px; 
                border: 1px solid #eee;
                border-radius: 2px;
              }
              .issue-tags { 
                margin-bottom: 3px; 
              }
              .severity-tag, .category-tag { 
                display: inline-block; 
                padding: 2px 6px; 
                margin-right: 5px; 
                border-radius: 2px; 
                color: white; 
                font-size: 11px; 
                font-weight: bold;
              }
              .issue-description { 
                font-size: 13px;
                line-height: 1.3;
              }
              .info-container { 
                margin-top: 8px; 
                padding: 5px; 
                background-color: #f9f9f9; 
                border-radius: 2px;
              }
              .info-title { 
                font-weight: bold; 
                margin-bottom: 5px; 
                color: #1976d2;
                font-size: 13px;
              }
              .info-item { 
                margin-bottom: 3px;
                font-size: 12px;
              }
              .details-list { 
                margin: 2px 0; 
                padding-left: 15px;
              }
              .details-list li {
                margin-bottom: 2px;
              }
              .no-issues {
                text-align: center;
                font-style: italic;
                color: #666;
                margin: 20px 0;
              }
              .damages-container {
                margin-left: 10px;
                margin-top: 8px;
              }
              .damage-item {
                margin: 5px 0;
                padding: 5px;
                background-color: #f8f9fa;
                border-left: 3px solid #dee2e6;
                border-radius: 3px;
              }
              .damage-description {
                display: flex;
                align-items: center;
                flex-wrap: wrap;
                gap: 5px;
                font-size: 13px;
              }
              .resolved-indicator {
                color: #4caf50;
                font-weight: bold;
                font-size: 11px;
              }
              .damage-tag {
                display: inline-block;
                padding: 1px 4px;
                background-color: #e3f2fd;
                border-radius: 8px;
                font-size: 10px;
                color: #1976d2;
              }
              @media print {
                body { margin: 0; font-size: 12px; }
                .group-container { break-inside: avoid; }
                .print-header h1 { font-size: 18px; }
              }
            </style>
          </head>
          <body>
            <div class="print-header">
              <h1>${intl.formatMessage({ id: 'AI_VERIFICATION_RESULTS' })}</h1>
            </div>
            
            <div class="filters-section">
              <div class="filters-title">${intl.formatMessage({ id: 'APPLIED_FILTERS' }) || 'Filtros Aplicados'}:</div>
              <div class="filter-item"><strong>${intl.formatMessage({ id: 'SEVERITY' })}:</strong> ${selectedSeverities.map(s => s.label).join(', ')}</div>
              <div class="filter-item"><strong>${intl.formatMessage({ id: 'CATEGORY' })}:</strong> ${selectedCategories.map(c => c.label).join(', ')}</div>
            </div>

            <div class="total-issues">
              ${totalIssues} ${intl.formatMessage({ id: totalIssues === 1 ? 'ISSUE' : 'ISSUES' })}
            </div>

            ${!validationResults || validationResults.length === 0 ? 
              `<div class="no-issues">${intl.formatMessage({ id: 'NO_ISSUES_FOUND' })}</div>` :
              validationResults.map(group => {
                const allGroupItems = group.items.filter(item =>
                  item.issues.some(issue =>
                    selectedSeverityValues.includes(issue.severity) && selectedCategoryValues.includes(issue.category)
                  )
                );
                
                if (allGroupItems.length === 0) return '';

                return `
                  <div class="group-container">
                    <div class="group-header">${group.index && group.index !== '0' ? `${group.index}. ` : ''}${group.group}</div>
                    ${allGroupItems.map(item => {
                      const visibleIssues = item.issues.filter(issue =>
                        selectedSeverityValues.includes(issue.severity) && selectedCategoryValues.includes(issue.category)
                      );

                      return `
                        <div class="item-container">
                          <div class="item-title">
                            ${item.index && item.index !== '0' ? `${item.index}. ` : ''}${item.name} (${visibleIssues.length} ${intl.formatMessage({ id: visibleIssues.length === 1 ? 'ISSUE' : 'ISSUES' })})
                          </div>
                          
                          ${visibleIssues.map(issue => `
                            <div class="issue-container">
                              <div class="issue-tags">
                                <span class="severity-tag" style="background-color: ${getSeverityColor(issue.severity)}">
                                  ${intl.formatMessage({ id: issue.severity })}
                                </span>
                                <span class="category-tag" style="background-color: ${getCategoryColor(issue.category)}">
                                  ${intl.formatMessage({ id: issue.category })}
                                </span>
                              </div>
                              <div class="issue-description">${issue.description}</div>
                            </div>
                          `).join('')}

                          ${item.info && (item.info.evaluation || item.info.details || item.info.observation || (item.info.damages && item.info.damages.length > 0)) ? `
                            <div class="info-container">
                              <div class="info-title">${intl.formatMessage({ id: 'INSPECTION_DATA' }) || 'Dados da Vistoria'}</div>
                              
                              ${item.info.damages && item.info.damages.length > 0 ? `
                                <div class="info-item">
                                  <strong>${intl.formatMessage({ id: 'INSPECTION_DAMAGES' }) || 'Apontamentos'}:</strong>
                                  <div class="damages-container">
                                    ${item.info.damages.map(damage => `
                                      <div class="damage-item">
                                        <div class="damage-description">
                                          <span>${damage.description}</span>
                                          ${damage.tags && damage.tags.length > 0 ? damage.tags.map(tag => `
                                            <span class="damage-tag">${tag}</span>
                                          `).join('') : ''}
                                          ${damage.resolved === true ? ` <span class="resolved-indicator">${intl.formatMessage({ id: 'RESOLVED' }) || '✓ Resolvido'}</span>` : ''}
                                        </div>
                                      </div>
                                    `).join('')}
                                  </div>
                                </div>
                              ` : ''}

                              ${item.info.evaluation ? `
                                <div class="info-item">
                                  <strong>${intl.formatMessage({ id: 'INSPECTION_EVALUATION' }) || 'Avaliação'}:</strong> 
                                  ${item.info.evaluation}
                                </div>
                              ` : ''}

                              ${item.info.details ? `
                                <div class="info-item">
                                  <strong>${intl.formatMessage({ id: 'INSPECTION_DETAILS' }) || 'Detalhes'}:</strong>
                                  <ul class="details-list">
                                    ${Object.entries(item.info.details).map(([key, value]) => `
                                      <li><strong>${key}:</strong> ${value}</li>
                                    `).join('')}
                                  </ul>
                                </div>
                              ` : ''}

                              ${item.info.observation ? `
                                <div class="info-item">
                                  <strong>${intl.formatMessage({ id: 'INSPECTION_OBSERVATION' }) || 'Observação'}:</strong> 
                                  ${item.info.observation}
                                </div>
                              ` : ''}
                            </div>
                          ` : ''}
                        </div>
                      `;
                    }).join('')}
                  </div>
                `;
              }).join('')}
          </body>
        </html>
      `;
      
      printWindow.document.write(printContent);
      printWindow.document.close();
      
      setTimeout(() => {
        printWindow.focus();
        
        setTimeout(() => {
          printWindow.print();
        }, 500);
      }, 100);

    } catch (error) {
      alert('Ocorreu um erro ao tentar imprimir. Verifique o console para mais detalhes.');
    }
  };

  const selectedSeverityValues = selectedSeverities.map(item => item.value);
  const selectedCategoryValues = selectedCategories.map(item => item.value);

  const filteredResults = validationResults?.filter(group => {
    const hasVisibleItems = group.items.some(item => {
      return item.issues.some(
        issue => selectedSeverityValues.includes(issue.severity) && selectedCategoryValues.includes(issue.category)
      );
    });
    return hasVisibleItems;
  });

  const totalIssues =
    validationResults?.reduce((total, group) => {
      return (
        total +
        group.items.reduce((groupTotal, item) => {
          return (
            groupTotal +
            item.issues.filter(
              issue =>
                selectedSeverityValues.includes(issue.severity) && selectedCategoryValues.includes(issue.category)
            ).length
          );
        }, 0)
      );
    }, 0) || 0;

  return (
    <div className={classes.contentModal}>
      <div className={classes.headerModal}>
        <span>{intl.formatMessage({ id: 'AI_VERIFICATION_RESULTS' })}</span>
        <IconButton onClick={onClose} className={classes.closeButton}>
          <Close />
        </IconButton>
      </div>
      <hr />

      <div className={classes.bodyModal}>
        {/* Filtros */}
        <div className={classes.filtersSection}>
          <div className={classes.filtersGrid}>
            <div className={classes.filtersColumn}>
              <div className={classes.filterGroup}>
                <span className={classes.filterLabel}>
                  {intl.formatMessage({ id: 'SEVERITY' })}
                </span>
                <Select
                  value={selectedSeverities}
                  onChange={handleSeverityChange}
                  options={severityOptions}
                  isMulti
                  components={animatedComponents}
                  className={classes.filterSelect}
                  classNamePrefix="select"
                  placeholder={intl.formatMessage({ id: 'SEVERITY' })}
                  noOptionsMessage={() => intl.formatMessage({ id: 'NO_ISSUES_FOUND' })}
                  formatOptionLabel={option => (
                    <div className={classes.optionLabel}>
                      <span
                        className={classes.colorIndicator}
                        style={{ backgroundColor: getSeverityColor(option.value) }}
                      />
                      {option.label}
                    </div>
                  )}
                />
              </div>

              <div className={classes.filterGroup}>
                <span className={classes.filterLabel}>
                  {intl.formatMessage({ id: 'CATEGORY' })}
                </span>
                <Select
                  value={selectedCategories}
                  onChange={handleCategoryChange}
                  options={categoryOptions}
                  isMulti
                  components={animatedComponents}
                  className={classes.filterSelect}
                  classNamePrefix="select"
                  placeholder={intl.formatMessage({ id: 'CATEGORY' })}
                  noOptionsMessage={() => intl.formatMessage({ id: 'NO_ISSUES_FOUND' })}
                  formatOptionLabel={option => (
                    <div className={classes.optionLabel}>
                      <span
                        className={classes.colorIndicator}
                        style={{ backgroundColor: getCategoryColor(option.value) }}
                      />
                      {option.label}
                    </div>
                  )}
                />
              </div>
            </div>

            <div className={classes.filtersActions}>
              <span className={classes.issuesCount}>
                {totalIssues} {intl.formatMessage({ id: totalIssues === 1 ? 'ISSUE' : 'ISSUES' })}
              </span>
              <ColorButton color="default" onClick={clearFilters}>
                {intl.formatMessage({ id: 'CLEAR_FILTERS' })}
              </ColorButton>
            </div>
          </div>
        </div>

        <hr className={classes.divider} />

        {/* Resultados */}
        <div className={classes.resultsSection}>
          {!filteredResults || filteredResults.length === 0 ? (
            <p className={classes.noIssues}>
              {intl.formatMessage({ id: 'NO_ISSUES_FOUND' })}
            </p>
          ) : (
            filteredResults.map((group, groupIndex) => {
              const visibleItems = group.items.filter(item =>
                item.issues.some(
                  issue =>
                    selectedSeverityValues.includes(issue.severity) && selectedCategoryValues.includes(issue.category)
                )
              );

              if (visibleItems.length === 0) return null;

              return (
                <div key={groupIndex} className={classes.groupContainer}>
                  <div className={classes.groupHeader} onClick={() => toggleGroup(group.group)}>
                    <span className={classes.groupTitle}>
                      {group.index && group.index !== '0' && `${group.index}. `}
                      {group.group}
                    </span>
                    {expandedGroups[group.group] ? <ExpandLess /> : <ExpandMore />}
                  </div>

                  <Collapse in={expandedGroups[group.group]} timeout="auto" unmountOnExit>
                    <div className={classes.groupContent}>
                      {visibleItems.map((item, itemIndex) => {
                        const itemKey = `${groupIndex}-${itemIndex}`;
                        const visibleIssues = item.issues.filter(
                          issue =>
                            selectedSeverityValues.includes(issue.severity) &&
                            selectedCategoryValues.includes(issue.category)
                        );

                        return (
                          <div key={itemIndex} className={classes.itemContainer}>
                            <div className={classes.itemHeader} onClick={() => toggleItem(itemKey)}>
                              <span className={classes.itemTitle}>
                                {item.index && item.index !== '0' && `${item.index}. `}
                                {item.name}
                                <span className={classes.issuesCount}>
                                  ({visibleIssues.length}{' '}
                                  {intl.formatMessage({ id: visibleIssues.length === 1 ? 'ISSUE' : 'ISSUES' })})
                                </span>
                              </span>
                              {expandedItems[itemKey] ? <ExpandLess /> : <ExpandMore />}
                            </div>

                            <Collapse in={expandedItems[itemKey]} timeout="auto" unmountOnExit>
                              <div className={classes.itemContent}>
                                {/* Issues */}
                                {visibleIssues.map((issue, issueIndex) => (
                                  <div key={issueIndex} className={classes.issueContainer}>
                                    <div className={classes.issueTags}>
                                      <span
                                        className={classes.severityTag}
                                        style={{ backgroundColor: getSeverityColor(issue.severity) }}
                                      >
                                        {intl.formatMessage({ id: issue.severity })}
                                      </span>
                                      <span
                                        className={classes.categoryTag}
                                        style={{ backgroundColor: getCategoryColor(issue.category) }}
                                      >
                                        {intl.formatMessage({ id: issue.category })}
                                      </span>
                                    </div>
                                    <p className={classes.issueDescription}>
                                      {issue.description}
                                    </p>
                                  </div>
                                ))}

                                {/* Info */}
                                {item.info && (item.info.evaluation || item.info.details || item.info.observation || (item.info.damages && item.info.damages.length > 0)) && (
                                  <div className={classes.infoContainer}>
                                    <span className={classes.infoTitle}>
                                      {intl.formatMessage({ id: 'INSPECTION_DATA' })}
                                    </span>

                                    {/* Apontamentos */}
                                    {item.info.damages && item.info.damages.length > 0 && (
                                      <div className={classes.infoItem}>
                                        <strong>{intl.formatMessage({ id: 'INSPECTION_DAMAGES' })}:</strong>
                                        <div className={classes.damagesContainer}>
                                          {item.info.damages.map((damage, damageIndex) => (
                                            <div key={damageIndex} className={classes.damageItem}>
                                              <div className={classes.damageDescription}>
                                                <span>{damage.description}</span>
                                                {damage.tags && damage.tags.length > 0 && 
                                                  damage.tags.map((tag, tagIndex) => (
                                                    <span key={tagIndex} className={classes.damageTag}>
                                                      {tag}
                                                    </span>
                                                  ))
                                                }
                                                {damage.resolved === true && (
                                                  <span className={classes.resolvedIndicator}>
                                                    {intl.formatMessage({ id: 'RESOLVED' })}
                                                  </span>
                                                )}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {item.info.evaluation && (
                                      <div className={classes.infoItem}>
                                        <strong>{intl.formatMessage({ id: 'INSPECTION_EVALUATION' })}:</strong>{' '}
                                        {item.info.evaluation}
                                      </div>
                                    )}

                                    {item.info.details && (
                                      <div className={classes.infoItem}>
                                        <strong>{intl.formatMessage({ id: 'INSPECTION_DETAILS' })}:</strong>
                                        <ul className={classes.detailsList}>
                                          {Object.entries(item.info.details).map(([key, value]) => (
                                            <li key={key}>
                                              <strong>{key}:</strong> {value}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}

                                    {item.info.observation && (
                                      <div className={classes.infoItem}>
                                        <strong>{intl.formatMessage({ id: 'INSPECTION_OBSERVATION' })}:</strong>{' '}
                                        {item.info.observation}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </Collapse>
                          </div>
                        );
                      })}
                    </div>
                  </Collapse>
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className={classes.footer}>
        <ColorButton color="primary" onClick={handlePrint} disabled={loading}>
          {intl.formatMessage({ id: 'PRINT_ANALYSIS' })}
        </ColorButton>
        <ColorButton color="default" onClick={onClose} isBusy={loading}>
          {intl.formatMessage({ id: 'CLOSE' })}
        </ColorButton>
      </div>
    </div>
  );
};

ModalAIVerification.propTypes = {
  onClose: PropTypes.func.isRequired,
  validationResults: PropTypes.array,
  loading: PropTypes.bool.isRequired,
};

ModalAIVerification.defaultProps = {
  validationResults: [],
};

export default ModalAIVerification;
